<template>
    <DCard
        :title="$t('settings_certificate_providers.form_title')"
        padding
    >
        <DFormRow
            :label="$t('settings_certificate_providers.type')"
            is-required
            :error="form.errors.type"
        >
            <DRadioGroup
                id="certificate_provider_type"
                v-model="form.type"
                :placeholder="$t('settings_certificate_providers.type')"
                :options="providerTypeOptions"
            />
        </DFormRow>

        <DFormRow
            :label="$t('settings_certificate_providers.uri')"
            is-required
            :error="form.errors.uri"
            class="mb-1"
        >
            <DInput
                id="certificate_provider_uri"
                v-model="form.uri"
                :placeholder="$t('settings_certificate_providers.uri')"
            />
        </DFormRow>

        <DRow class="mt-2 pt-0 mb-4">
            <DCol sm="4" />
            <DCol
                sm="4"
                class="check-database-connection-btn"
            >
                <div class="d-flex flex-row align-items-center justify-content-start gap-4">
                    <DButton
                        icon="lan-connect"
                        :label="$t('common.check_connection')"
                        :disabled="isCheckConnectionLoading"
                        @click="checkConnection"
                    />
                    <div
                        v-show="connectionStatus === 'successful'"
                        class="green-text"
                    >
                        {{ $t('common.connection_successful') }}
                    </div>
                    <div
                        v-show="connectionStatus === 'unsuccessful'"
                        class="red-text"
                    >
                        {{ $t('common.connection_unsuccessful') }}
                    </div>
                </div>
            </DCol>
        </DRow>

        <DFormRow
            :label="$t('settings_certificate_providers.username')"
            is-required
            :error="form.errors.user"
        >
            <DInput
                id="certificate_provider_user"
                v-model="form.user"
                :placeholder="$t('settings_certificate_providers.username')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            :label="$t('settings_certificate_providers.password')"
            is-required
            :error="form.errors.password"
        >
            <DInput
                id="certificate_provider_password"
                v-model="form.password"
                type="password"
                :placeholder="$t('settings_certificate_providers.password')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="$t('settings_certificate_providers.token')"
            is-required
            :error="form.errors.token"
        >
            <DInput
                id="certificate_provider_token"
                v-model="form.token"
                type="password"
                :placeholder="$t('settings_certificate_providers.token')"
                maxlength="255"
            />
        </DFormRow>
    </DCard>

    <DFabs>
        <DFormControls
            :dirty="form.isDirty"
            @submit="submitForm"
        >
            <template #additional_button>
                <DFab
                    v-if="provider?.id !== undefined"
                    icon="delete"
                    :label="$t('common.remove')"
                    color="danger"
                    @click="openDeleteModal"
                />
            </template>
        </DFormControls>
    </DFabs>

    <CertificateProviderDeleteModal
        :show-modal="showDeleteModal"
        @close-delete-modal="closeDeleteModal"
        @delete-confirmed="deleteProvider"
    />
</template>

<script setup>
import { router, useForm } from '@inertiajs/vue3'
import CertificateProviderDeleteModal from './DeleteModal.vue'
import { ref } from 'vue'
import axios from 'axios'

const props = defineProps({
    provider: {
        type: Object,
        default: () => ({})
    },
    providerTypeOptions: {
        type: Array,
        default: () => []
    }
})

const form = useForm({
    type: props.provider?.type ?? props.providerTypeOptions[Object.keys(props.providerTypeOptions)[0]].key,
    uri: props.provider?.uri,
    user: props.provider?.user,
    password: props.provider?.password,
    token: props.provider?.token
})

const submitForm = () => {
    if (props.provider?.id !== undefined) {
        form.patch(route('settings.certificate-providers.update', { certificate_provider: props.provider?.id }))
    } else {
        form.post(route('settings.certificate-providers.store'))
    }
}

const showDeleteModal = ref(false)
const deleteItem = ref(null)
const connectionStatus = ref(undefined)

const closeDeleteModal = () => {
    showDeleteModal.value = false
    deleteItem.value = null
}

const openDeleteModal = (item = null) => {
    if (item !== null) {
        deleteItem.value = item
    }
    showDeleteModal.value = true
}

const deleteProvider = () => {
    router.delete(route('settings.certificate-providers.destroy', { certificate_provider: props.provider.id }))

    closeDeleteModal()
}

const isCheckConnectionLoading = ref(false)
const checkConnection = () => {
    isCheckConnectionLoading.value = true

    axios.post(route('settings.certificate-providers.check-connection', form))
        .then(function () {
            isCheckConnectionLoading.value = false
            connectionStatus.value = 'successful'
        })
        .catch(function (error) {
            isCheckConnectionLoading.value = false
            connectionStatus.value = 'unsuccessful'

            if (error.response && error.response.data && error.response.data.errors) {
                form.setError(error.response.data.errors)
            }
        })
}
</script>

<style scoped lang="scss">
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
